import type { AirplaneModel } from './airplanes';
import { SeatClassIndex } from '../constants/server-fleets';

type CabinBlockKind = 'seat' | 'facility';

interface SlotConfig {
  seat_product_id: string;
}

export interface CabinBlock {
  kind: CabinBlockKind;
  slot?: 0 | 1 | 2;
  facility_id?: string;
}

export interface CabinConfig {
  slot_configs: [SlotConfig, SlotConfig, SlotConfig];
  cabin_crew_count: number;
  editor_blocks: CabinBlock[];
}

export interface CompiledCabinMetrics {
  used_length: number; // cm
  seat_rows_by_slot: [number, number, number];
  seats_by_slot: [number, number, number];
  comfort_by_slot: [number, number, number];
  service_cost_by_slot: [number, number, number];
  install_weight: number; // kg
  people_weight: number; // kg
  fuel_load: number; // kg
  fuel_burn: number; // kg per km
  configured_range: number; // km
  active_slot_count: number;
}

interface SeatProductDef {
  id: string;
  seats_per_row_by_body: Record<AirplaneModel['body_type'], number>;
  fixture_weight: number; // kg
  legroom: number; // cm
  base_comfort: number;
  service_cost: number; // dollars per passenger
}

interface FacilityBlockDef {
  id: string;
  length: number; // cm
  fixture_weight: number; // kg
  comfort_bonus_by_slot: [number, number, number];
}

export const seatProducts: Record<string, SeatProductDef> = {
  dense: {
    id: 'dense',
    seats_per_row_by_body: { regional: 4, narrow: 6, wide_9across: 9, wide_10across: 10 },
    fixture_weight: 10,
    legroom: 71,
    base_comfort: 42,
    service_cost: 4,
  },
  standard: {
    id: 'standard',
    seats_per_row_by_body: { regional: 4, narrow: 6, wide_9across: 9, wide_10across: 10 },
    fixture_weight: 12,
    legroom: 79,
    base_comfort: 52,
    service_cost: 6,
  },
  extra_legroom: {
    id: 'extra_legroom',
    seats_per_row_by_body: { regional: 4, narrow: 6, wide_9across: 9, wide_10across: 10 },
    fixture_weight: 13,
    legroom: 91,
    base_comfort: 65,
    service_cost: 9,
  },
  recliner: {
    id: 'recliner',
    seats_per_row_by_body: { regional: 4, narrow: 4, wide_9across: 7, wide_10across: 8 },
    fixture_weight: 18,
    legroom: 102,
    base_comfort: 74,
    service_cost: 16,
  },
  lieflat: {
    id: 'lieflat',
    seats_per_row_by_body: { regional: 2, narrow: 4, wide_9across: 6, wide_10across: 7 },
    fixture_weight: 25,
    legroom: 122,
    base_comfort: 84,
    service_cost: 28,
  },
  suite: {
    id: 'suite',
    seats_per_row_by_body: { regional: 1, narrow: 2, wide_9across: 4, wide_10across: 4 },
    fixture_weight: 40,
    legroom: 145,
    base_comfort: 92,
    service_cost: 60,
  },
};

export const facilityBlocks: Record<string, FacilityBlockDef> = {
  lav_standard: {
    id: 'lav_standard',
    length: 120,
    fixture_weight: 200,
    comfort_bonus_by_slot: [1, 1, 1],
  },
  lav_premium: {
    id: 'lav_premium',
    length: 140,
    fixture_weight: 240,
    comfort_bonus_by_slot: [1, 2, 3],
  },
  galley_compact: {
    id: 'galley_compact',
    length: 120,
    fixture_weight: 250,
    comfort_bonus_by_slot: [0, 1, 1],
  },
  galley_full: {
    id: 'galley_full',
    length: 180,
    fixture_weight: 400,
    comfort_bonus_by_slot: [1, 2, 2],
  },
  lounge_small: {
    id: 'lounge_small',
    length: 260,
    fixture_weight: 450,
    comfort_bonus_by_slot: [0, 1, 4],
  },
};

function defaultSeatProductForSlot(slot: 0 | 1 | 2): string {
  if (slot === 1) return 'recliner';
  if (slot === 2) return 'lieflat';
  return 'standard';
}

// ─── Section-based editing helpers ───
// The server compiles cabins from block COUNTS (rows per slot, facilities per id);
// block order is purely cosmetic. These helpers let the UI edit counts directly
// and regenerate a canonical, nice-looking block order for the visualization.
// A slot is a seat class (SeatClassIndex, fleets.go's ECON, BUSINESS, FIRST), and
// these name the class they mean; the compile below mirrors cabin.go, which
// indexes slots by number, and does the same.

export interface CabinCounts {
  rowCounts: [number, number, number];
  facilityCounts: Record<string, number>;
}

/** Collapse an editor block list into per-slot row counts and facility counts. */
export function deriveCabinCounts(blocks: CabinBlock[]): CabinCounts {
  const rowCounts: [number, number, number] = [0, 0, 0];
  const facilityCounts: Record<string, number> = {};
  for (const block of blocks) {
    if (block.kind === 'seat') {
      rowCounts[block.slot ?? SeatClassIndex.ECON] += 1;
    } else if (block.facility_id) {
      facilityCounts[block.facility_id] = (facilityCounts[block.facility_id] ?? 0) + 1;
    }
  }
  return { rowCounts, facilityCounts };
}

/** Expand a facility count map into an ordered list of ids, preferring `order`. */
function facilityPool(facilityCounts: Record<string, number>, order: string[]): string[] {
  const pool: string[] = [];
  for (const id of order) {
    for (let i = 0; i < (facilityCounts[id] ?? 0); i += 1) pool.push(id);
  }
  return pool;
}

/**
 * Build a canonical front-to-back block order from section counts:
 * galley at the nose, premium cabins forward (first, business, economy),
 * lounge behind the premium section, lavatories distributed between cabins,
 * leftovers at the rear. Order does not affect compiled metrics — this only
 * shapes the visualization.
 */
export function buildEditorBlocks(rowCounts: [number, number, number], facilityCounts: Record<string, number>): CabinBlock[] {
  const galleys = facilityPool(facilityCounts, ['galley_full', 'galley_compact']);
  const lavs = facilityPool(facilityCounts, ['lav_premium', 'lav_standard']);
  const lounges = facilityPool(facilityCounts, ['lounge_small']);
  const blocks: CabinBlock[] = [];

  const pushFacility = (id: string | undefined) => {
    if (id) blocks.push({ kind: 'facility', facility_id: id });
  };

  // Front galley
  pushFacility(galleys.shift());

  const premiumForward = [SeatClassIndex.FIRST, SeatClassIndex.BUSINESS, SeatClassIndex.ECON] as const;
  const frontMostSlot = premiumForward.find((slot) => rowCounts[slot] > 0);
  for (const slot of premiumForward) {
    if (rowCounts[slot] === 0) continue;
    for (let i = 0; i < rowCounts[slot]; i += 1) {
      blocks.push({ kind: 'seat', slot });
    }
    // Lounge sits directly behind the most premium cabin
    if (slot === frontMostSlot) {
      while (lounges.length) pushFacility(lounges.shift());
    }
    // One lavatory between cabins (premium lavs forward)
    pushFacility(lavs.shift());
  }

  // Remaining facilities at the rear
  while (galleys.length) pushFacility(galleys.shift());
  while (lounges.length) pushFacility(lounges.shift());
  while (lavs.length) pushFacility(lavs.shift());

  return blocks;
}

export function getSeatProductLegroom(productId: string): number {
  return seatProducts[productId]?.legroom ?? 79;
}

function clampComfort(value: number): number {
  return Math.max(0, Math.min(100, Math.round(value)));
}

function totalInstalledSeats(seats: [number, number, number]): number {
  return seats[0] + seats[1] + seats[2];
}

export function defaultCabinConfig(model: AirplaneModel): CabinConfig {
  const productId = 'standard';
  const product = seatProducts[productId];
  const seatsPerRow = product?.seats_per_row_by_body[model.body_type] ?? 6;
  const facilityCounts = { galley_compact: 1, lav_standard: 2 };
  const facilityLength =
    facilityBlocks.galley_compact.length + 2 * facilityBlocks.lav_standard.length;

  // Target enough rows for max_seats, but never overflow the cabin.
  const targetRows = Math.max(1, Math.ceil((model.max_seats || seatsPerRow) / seatsPerRow));
  const maxRows = Math.floor((model.cabin_length - facilityLength) / (product?.legroom ?? 79));
  const rowCount = Math.max(1, Math.min(targetRows, maxRows));

  return {
    slot_configs: [
      { seat_product_id: 'standard' },
      { seat_product_id: 'recliner' },
      { seat_product_id: 'lieflat' },
    ],
    cabin_crew_count: 2,
    editor_blocks: buildEditorBlocks([rowCount, 0, 0], facilityCounts),
  };
}

export function compileCabinConfig(model: AirplaneModel, config: CabinConfig): { metrics: CompiledCabinMetrics | null; error: string | null } {
  const seatRowsBySlot: [number, number, number] = [0, 0, 0];
  const seatsBySlot: [number, number, number] = [0, 0, 0];
  const comfortBySlot: [number, number, number] = [0, 0, 0];
  const serviceCostBySlot: [number, number, number] = [0, 0, 0];
  const facilityCounts = new Map<string, number>();
  let usedLength = 0;
  let installWeight = 0;

  for (const block of config.editor_blocks) {
    if (block.kind === 'seat') {
      const slot = block.slot ?? 0;
      if (slot < 0 || slot > 2) {
        return { metrics: null, error: 'Invalid seat block slot.' };
      }
      seatRowsBySlot[slot] += 1;
    } else {
      if (!block.facility_id || !facilityBlocks[block.facility_id]) {
        return { metrics: null, error: 'Unknown facility block.' };
      }
      facilityCounts.set(block.facility_id, (facilityCounts.get(block.facility_id) ?? 0) + 1);
    }
  }

  if (config.editor_blocks.length === 0) {
    return { metrics: null, error: 'Cabin configuration cannot be empty.' };
  }

  for (const slot of [0, 1, 2] as const) {
    const slotConfig = { ...config.slot_configs[slot] };
    if (!slotConfig.seat_product_id) {
      slotConfig.seat_product_id = defaultSeatProductForSlot(slot);
    }

    const product = seatProducts[slotConfig.seat_product_id];
    if (!product) {
      return { metrics: null, error: `Unknown seat product for slot ${slot + 1}.` };
    }
    const seatsPerRow = product.seats_per_row_by_body[model.body_type];
    if (!seatsPerRow) {
      return { metrics: null, error: `Seat product ${slotConfig.seat_product_id} does not support this body type.` };
    }
    seatsBySlot[slot] = seatsPerRow * seatRowsBySlot[slot];
    usedLength += seatRowsBySlot[slot] * product.legroom;
    installWeight += seatsBySlot[slot] * product.fixture_weight;
    serviceCostBySlot[slot] = Math.max(0, product.service_cost + model.cabin_service_cost_modifier);
    comfortBySlot[slot] = clampComfort(product.base_comfort);
  }

  const totalSeatsBeforeCapacityMod = totalInstalledSeats(seatsBySlot);
  if (model.cabin_capacity_modifier !== 0 && totalSeatsBeforeCapacityMod > 0) {
    const extraSeats = Math.floor(totalSeatsBeforeCapacityMod * model.cabin_capacity_modifier);
    if (extraSeats > 0) {
      let targetSlot: 0 | 1 | 2 = 0;
      for (const slot of [0, 1, 2] as const) {
        if (seatsBySlot[slot] > 0) {
          targetSlot = slot;
          break;
        }
      }
      seatsBySlot[targetSlot] += extraSeats;
      const product = seatProducts[config.slot_configs[targetSlot]?.seat_product_id || defaultSeatProductForSlot(targetSlot)];
      installWeight += extraSeats * product.fixture_weight;
    }
  }

  for (const [facilityId, count] of facilityCounts.entries()) {
    const facility = facilityBlocks[facilityId];
    usedLength += facility.length * count;
    installWeight += facility.fixture_weight * count;
    ([0, 1, 2] as const).forEach((slot) => {
      if (seatsBySlot[slot] > 0) {
        comfortBySlot[slot] = clampComfort(comfortBySlot[slot] + facility.comfort_bonus_by_slot[slot] * count);
      }
    });
  }

  if (usedLength > model.cabin_length) {
    return { metrics: null, error: `Cabin uses ${usedLength} cm of ${model.cabin_length} cm.` };
  }

  const activeSlotCount = seatsBySlot.filter((count) => count > 0).length;
  const minCrew = activeSlotCount === 0 ? 0 : 1 + activeSlotCount;
  if (config.cabin_crew_count < minCrew) {
    return { metrics: null, error: `Cabin crew must be at least ${minCrew}.` };
  }
  if (config.cabin_crew_count > 12) {
    return { metrics: null, error: 'Cabin crew cannot exceed 12.' };
  }

  const totalSeats = seatsBySlot[0] + seatsBySlot[1] + seatsBySlot[2];
  const crewBonus = totalSeats > 0 ? Math.min(10, 120 * config.cabin_crew_count / totalSeats) : 0;
  ([0, 1, 2] as const).forEach((slot) => {
    if (seatsBySlot[slot] === 0) {
      comfortBySlot[slot] = 0;
      return;
    }

    let comfort = comfortBySlot[slot] + crewBonus;
    if (model.cabin_comfort_modifier !== 0) {
      comfort *= 1 + model.cabin_comfort_modifier;
    }
    comfortBySlot[slot] = clampComfort(comfort);
  });

  const peopleWeight = (totalSeats + model.pilot_count + config.cabin_crew_count) * model.average_person_weight;
  const fuelLoad = Math.max(0, Math.min(model.fuel_capacity, model.mtow - model.oew - installWeight - peopleWeight));
  const avgWeight = model.oew + installWeight + peopleWeight + fuelLoad * 0.5;
  const fuelBurn = model.fuel_burn_base + model.fuel_burn_weight * (avgWeight / 1000);
  const configuredRange = fuelBurn > 0 ? Math.round(fuelLoad / fuelBurn) : 0;

  return {
    metrics: {
      used_length: usedLength,
      seat_rows_by_slot: seatRowsBySlot,
      seats_by_slot: seatsBySlot,
      comfort_by_slot: comfortBySlot,
      service_cost_by_slot: serviceCostBySlot,
      install_weight: installWeight,
      people_weight: peopleWeight,
      fuel_load: fuelLoad,
      fuel_burn: fuelBurn,
      configured_range: configuredRange,
      active_slot_count: activeSlotCount,
    },
    error: null,
  };
}
